// src/lib/query/persister.ts
/**
 * @fileoverview React Query localStorage persister configuration
 */

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

import { isCacheVersionValid, clearStaleCache, updateCacheVersion } from './cacheVersion';
import { shouldPersistQuery } from './persistenceRules';
import { GC_TIMES } from './staleTime';

const STORAGE_KEY = 'REACT_QUERY_OFFLINE_CACHE';

/**
 * Maximum age for persisted cache (1 hour)
 */
export const PERSIST_MAX_AGE = GC_TIMES.LONG;

const serialize = (client: PersistedClient): string => {
  const filteredClient: PersistedClient = {
    ...client,
    clientState: {
      ...client.clientState,
      queries: client.clientState.queries.filter(query => {
        const queryForFilter = {
          queryKey: query.queryKey,
          state: query.state,
          meta: query.meta,
        };
        return shouldPersistQuery(queryForFilter as any);
      }),
      mutations: [],
    },
  };
  return JSON.stringify(filteredClient);
};

const deserialize = (cachedString: string): PersistedClient => {
  try {
    if (!isCacheVersionValid()) {
      clearStaleCache();
      return { clientState: { queries: [], mutations: [] }, timestamp: Date.now(), buster: '' };
    }
    return JSON.parse(cachedString) as PersistedClient;
  } catch (error) {
    console.warn('[Persister] Failed to deserialize cache:', error);
    return { clientState: { queries: [], mutations: [] }, timestamp: Date.now(), buster: '' };
  }
};

const storage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
      updateCacheVersion();
    } catch (error) {
      console.warn('[Persister] Storage full, persistence disabled');
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {}
  },
};

/**
 * Creates the localStorage persister instance
 */
export const createPersister = (): Persister => {
  return createSyncStoragePersister({
    storage,
    key: STORAGE_KEY,
    serialize,
    deserialize,
  });
};

/**
 * Clears all persisted React Query cache
 */
export const clearPersistedCache = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('rq-cache-version');
    clearStaleCache();
  } catch {}
};

/**
 * Gets the current user ID from persisted cache
 */
export const getCachedUserId = (): string | null => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (!cached) return null;

    const client: PersistedClient = JSON.parse(cached);
    const userQuery = client.clientState.queries.find(
      q => Array.isArray(q.queryKey) && q.queryKey[0] === 'users' && q.queryKey[1] === 'profile'
    );

    return (userQuery?.state?.data as any)?.id || null;
  } catch {
    return null;
  }
};
